<script setup>
import { computed } from 'vue'
import { Ban, Copy, Ellipsis, Pencil, Printer, QrCode, ShieldCheck, Trash2 } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import JoinedMembers from './JoinedMembers.vue'
import JoinRequests from './JoinRequests.vue'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { limitItems } from './linkMeta.js'

const props = defineProps({
  link: { type: Object, required: true },
  removable: { type: Boolean, default: false },
  canManage: { type: Boolean, default: true },
  manageable: { type: Boolean, default: false },
})
const emit = defineEmits(['copy', 'qr', 'print', 'edit', 'revoke', 'delete', 'remove', 'approve', 'decline'])

const title = computed(() => props.link.name || 'Join link')
const limits = computed(() => limitItems(props.link))
</script>

<template>
  <div v-if="link.is_revoked" class="flex items-center gap-3 rounded-xl border bg-card p-3 opacity-60" data-testid="join-link-card">
    <div class="min-w-0 flex-1">
      <p class="truncate text-sm font-medium">{{ title }}</p>
      <div class="mt-0.5 flex items-center gap-x-1.5 text-xs text-muted-foreground">
        <JoinedMembers :joined="link.joined" size="xs" :removable="removable" @remove="emit('remove', $event)" />
        <span>·</span>
        <span>Revoked</span>
      </div>
    </div>
    <DropdownMenu v-if="canManage">
      <DropdownMenuTrigger as-child>
        <Button variant="ghost" class="size-8 shrink-0 rounded-full p-0" aria-label="Link actions">
          <Ellipsis class="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" class="w-44">
        <DropdownMenuItem class="cursor-pointer text-destructive focus:text-destructive" @click="emit('delete')">
          <Trash2 class="mr-2 size-4" />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  </div>

  <div v-else class="flex items-center gap-3 rounded-xl border bg-card p-3" data-testid="join-link-card">
    <div class="min-w-0 flex-1">
      <div class="flex min-w-0 items-center gap-2">
        <TooltipProvider v-if="link.requires_approval" :delay-duration="150">
          <Tooltip>
            <TooltipTrigger as-child>
              <ShieldCheck class="-mr-1 size-4 shrink-0 cursor-default text-muted-foreground" aria-label="Moderation" data-testid="join-link-moderation" />
            </TooltipTrigger>
            <TooltipContent side="top">Moderation: new members join after approval</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <p class="truncate text-sm font-semibold">{{ title }}</p>
        <span class="shrink-0 text-xs text-muted-foreground" data-testid="join-link-role">{{ link.role_name }}</span>
        <TooltipProvider v-if="limits.length" :delay-duration="150">
          <div class="flex shrink-0 items-center gap-1" data-testid="join-link-meta">
            <Tooltip v-for="item in limits" :key="item.key">
              <TooltipTrigger as-child>
                <span
                  class="inline-flex h-5 min-w-5 cursor-default items-center justify-center gap-0.5 rounded-md px-1 text-xs tabular-nums"
                  :class="item.warn ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' : 'text-muted-foreground'"
                  :aria-label="item.text"
                >
                  <component :is="item.icon" v-if="item.icon" class="size-3.5" />
                  <span v-if="item.label">{{ item.label }}</span>
                </span>
              </TooltipTrigger>
              <TooltipContent side="top">{{ item.text }}</TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>
      </div>
      <div class="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
        <JoinRequests
          :requests="link.requests || []"
          size="xs"
          :manageable="manageable"
          @approve="emit('approve', $event)"
          @decline="emit('decline', $event)"
        />
        <JoinedMembers :joined="link.joined" size="xs" :removable="removable" @remove="emit('remove', $event)" />
      </div>
    </div>
    <div class="flex shrink-0 items-center gap-1.5">
      <Button type="button" variant="secondary" size="sm" class="h-7 px-2 text-xs" @click="emit('qr')">
        <QrCode class="mr-1 size-3.5" />
        QR
      </Button>
      <Button type="button" variant="secondary" size="sm" class="h-7 px-2 text-xs" @click="emit('print')">
        <Printer class="mr-1 size-3.5" />
        Print
      </Button>
      <Button type="button" size="sm" class="h-7 px-2 text-xs" :title="link.url" data-testid="join-link-copy" @click="emit('copy')">
        <Copy class="mr-1 size-3.5" />
        Copy
      </Button>
      <DropdownMenu v-if="canManage">
        <DropdownMenuTrigger as-child>
          <Button variant="ghost" class="size-7 shrink-0 rounded-full p-0" aria-label="Link actions">
            <Ellipsis class="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" class="w-44">
          <DropdownMenuItem class="cursor-pointer" @click="emit('edit')">
            <Pencil class="mr-2 size-4" />
            Edit
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem class="cursor-pointer text-destructive focus:text-destructive" @click="emit('revoke')">
            <Ban class="mr-2 size-4" />
            Revoke
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  </div>
</template>
